import {
  cancelBulkTransaction,
  getBulkTransations,
  getTransactionJob,
  resumeBulkTransaction,
} from 'admin/actions/rewards';
import CSVButton from 'admin/components/shared/CSVButton';
import Pager from 'admin/components/shared/Pager';
import { formatDate, formatJstFromUtc, formatUUID, toQueryParams } from 'admin/utils';
import React, { useCallback, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import styled from 'styled-components';
import { openAlertModal } from '~/javascripts/admin/actions/alert-modal';
import { useAppDispatch, useAppSelector } from '~/javascripts/admin/hooks/useApp';
import { createBadge } from '../../../atoms/Badge';
import { Button } from '../../../atoms/Buttons';
import FA from '../../../atoms/FontAwesome';
import Loader from '../../../atoms/Loader';
import Table from '../../../atoms/Table';
import Tooltip from '../../../atoms/Tooltip';

const LoaderWrapper = styled.div`
  width: 100%;
  height: 150px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const StatusWrapper = styled.div`
  display: flex;
  align-items: center;
`;

const RefreshBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 12px;
`;

const InProgressNote = styled.span`
  color: #3c86c9;
  font-size: 0.875rem;
`;

const ActionButtonsWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

// 受付〜処理中で状態が今後変化しうるステータス(これらがある間は自動更新する)
// receiving = CSVアップロード受付中の一時状態(record-first でDBに先行コミットされ一覧に出る)
const IN_PROGRESS_STATUSES = ['receiving', 'submitted', 'examining', 'queued', 'processing'];
const POLL_INTERVAL_MS = 8000;

const CANCELABLE_STATUSES = [
  'transaction-scheduled',
  'transaction-receiving',
  'transaction-submitted',
  'transaction-examining',
  'transaction-queued',
  'transaction-processing',
];

const CANCELATION_BEHAVIORS = {
  'transaction-scheduled': {
    label: 'キャンセル',
    skipConfirmation: false,
  },
  'transaction-receiving': {
    label: 'キャンセル',
    skipConfirmation: true,
  },
  'transaction-submitted': {
    label: 'キャンセル',
    skipConfirmation: true,
  },
  'transaction-examining': {
    label: (
      <>
        <FA icon="stop" /> 停止
      </>
    ),
    skipConfirmation: true,
  },
  'transaction-queued': {
    label: (
      <>
        <FA icon="stop" /> 停止
      </>
    ),
    skipConfirmation: true,
  },
  'transaction-processing': {
    label: (
      <>
        <FA icon="stop" /> 停止
      </>
    ),
    skipConfirmation: true,
  },
};

const CountWrapper = styled.span`
  display: inline-block;
  color: ${(props) => (props.isError ? props.theme.colors.red['300'] : undefined)};
`;

const getTransactionStatus = (transaction) => {
  // receiving submitted examining queued processing error done scheduled canceled
  if (!transaction) {
    return {};
  }
  if (transaction.status === 'receiving') {
    // CSVアップロードの受付処理中(まだ受付確定前)。この間もDBに載るため一覧に表示される。
    return { name: 'transaction-receiving', label: '受付処理中' };
  }
  if (transaction.status === 'submitted') {
    return { name: 'transaction-submitted', label: '申請済' };
  }
  if (transaction.status === 'examining') {
    return { name: 'transaction-examining', label: '内容確認中' };
  }
  if (transaction.status === 'queued') {
    return { name: 'transaction-queued', label: '処理待ち' };
  }
  if (transaction.status === 'processing') {
    return { name: 'transaction-processing', label: '処理中' };
  }
  if (transaction.status === 'error') {
    if (transaction.success_count !== null && transaction.total_count !== null) {
      return { name: 'transaction-done', label: '実行済' };
    }
    return { name: 'transaction-error', label: 'エラー' };
  }
  if (transaction.status === 'done') {
    return { name: 'transaction-done', label: '実行済' };
  }
  if (transaction.status === 'scheduled') {
    return { name: 'transaction-scheduled', label: '予約' };
  }
  if (transaction.status === 'canceled') {
    // 予約の取消と、実行途中の中断のどちらもこの status になる
    return { name: 'transaction-canceled', label: '処理中断' };
  }
  return {};
};

const parseErrorCode = (code) => {
  if (code === 'invalid_csv_format') {
    return 'CSV ファイルが無効です';
  }
  if (code === 'account_not_exist') {
    return 'アカウントが存在しない';
  }
  if (code === 'not_accessible_private_money') {
    return 'マネーにアクセスできない';
  }
  return code;
};

const HEADERS = [
  { label: '状態', size: 1.5, align: 'left' },
  { label: 'ID', size: 1.5, align: 'left' },
  { label: '付与事由', size: 2, align: 'left' },
  { label: '登録日時', size: 1, align: 'left' },
  { label: '最終更新日時', size: 1, align: 'left' },
  { label: '予約日時', size: 1, align: 'left' },
  { label: '作成者', size: 1.6, align: 'left' },
  { label: '付与人数', size: 1, align: 'right' },
  { label: '', size: 1.4, align: 'right' },
];

const RewardStatusBadge = createBadge({
  'transaction-receiving': { label: '受付処理中', bgColor: '#3c86c94d' },
  'transaction-submitted': { label: '申請済', bgColor: '#3c86c94d' },
  'transaction-examining': { label: '内容確認中', bgColor: '#3c86c94d' },
  'transaction-queued': { label: '処理待ち', bgColor: '#3c86c94d' },
  'transaction-processing': { label: '処理中', bgColor: '#3c86c94d' },
  'transaction-error': { label: 'エラー', bgColor: '#d75f48' },
  'transaction-done': { label: '実行済', bgColor: '#208637' },
  'transaction-scheduled': { label: '予約', bgColor: '#3C86C9' },
  'transaction-canceled': { label: '処理中断', bgColor: '#3c86c94d' },
});

const StyledTooltip = styled(Tooltip)`
  opacity: 1 !important;
`;

const DirectRewards = ({ selectedMoneyId, setApiResult }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [transactions, setTransations] = useState({
    rows: [],
    pagination: { current: 1, per_page: 50 },
  });
  const dispatch = useAppDispatch();

  // silent=true のときは全画面ローダーを出さずに裏で更新する(自動ポーリング用)
  const fetchTransactions = useCallback(
    (page = 1, { silent = false } = {}) => {
      if (!selectedMoneyId) return undefined;

      if (!silent) setIsLoading(true);

      const params = {
        page,
        per_page: transactions.pagination.per_page,
        private_money_id: selectedMoneyId,
      };

      return getBulkTransations(toQueryParams(params))
        .then(setTransations)
        .finally(() => {
          if (!silent) setIsLoading(false);
        });
    },
    [transactions.pagination.per_page, selectedMoneyId]
  );

  useEffect(() => {
    fetchTransactions();
  }, [selectedMoneyId]);

  const currentPage = transactions.pagination?.current ?? 1;
  const hasInProgress = transactions.rows.some((r) => IN_PROGRESS_STATUSES.includes(r.status));

  // 受付〜処理中のバッチがある間だけ自動でポーリングして状態変化を反映する
  // (一覧はデフォルトでは自動更新されないため、受付直後の状態遷移が見えるようにする)
  useEffect(() => {
    if (!hasInProgress) return undefined;
    const timer = setInterval(() => {
      fetchTransactions(currentPage, { silent: true });
    }, POLL_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [hasInProgress, currentPage, fetchTransactions]);

  const makeTransactionError = (transaction) => {
    const { error, error_lineno: errorLine } = transaction;

    const description = parseErrorCode(error);
    // errorLine sometime return empty array ([]) as opposed to a number or null
    const line = !errorLine || Array.isArray(errorLine) ? '' : `(${errorLine} 行)`;

    if (!description && !line) return '';

    return (
      <span>
        {description} {line}
      </span>
    );
  };

  return (
    <>
      <RefreshBar>
        {hasInProgress && <InProgressNote>処理中のバッチがあります（自動更新中…）</InProgressNote>}
        <Button
          variant="muted-outline"
          onClick={() => fetchTransactions(currentPage)}
          disabled={isLoading}
        >
          更新
        </Button>
      </RefreshBar>
      {isLoading ? (
        <LoaderWrapper>
          <Loader type="Oval" width={50} height={50} />
        </LoaderWrapper>
      ) : (
        <Table styles={{ marginTop: '20px' }}>
          <Table.Header>
            <Table.Row>
              {HEADERS.map((h) => (
                <Table.Cell size={h.size} key={h.label} align={h.align}>
                  {h.label}
                </Table.Cell>
              ))}
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {transactions.rows.map((row) => {
              const status = getTransactionStatus(row);
              const error = row.error ? makeTransactionError(row) : '';

              return (
                <Table.Row>
                  <Table.Cell wordBreak="keep-all">
                    <StatusWrapper>
                      <RewardStatusBadge status={status.name} />

                      {status.name === 'transaction-error' && !!error && (
                        <StyledTooltip
                          id={`error-${row.id}`}
                          btnText="!"
                          btnStyles={{ backgroundColor: '#d75f48' }}
                          text={error}
                          type="error"
                          backgroundColor="#FECACA"
                          textColor="#F00"
                        />
                      )}
                    </StatusWrapper>
                  </Table.Cell>
                  <Table.Cell wordBreak="keep-all">{formatUUID(row.id)}</Table.Cell>
                  <Table.Cell wordBreak="break-word">{row.name}</Table.Cell>
                  <Table.Cell>{formatDate(row.submitted_at)}</Table.Cell>
                  <Table.Cell>{formatDate(row.updated_at)}</Table.Cell>
                  <Table.Cell>{formatDate(row.scheduled_at, 'YYYY/MM/DD HH:mm')}</Table.Cell>
                  <Table.Cell wordBreak="break-word">{row.operator}</Table.Cell>
                  <Table.Cell wordBreak="break-word" align="right">
                    {row.success_count !== null && row.total_count !== null ? (
                      <>
                        <CountWrapper isError={row.success_count !== row.total_count}>
                          {row.success_count.toLocaleString()}
                        </CountWrapper>
                        <CountWrapper>/{row.total_count.toLocaleString()}</CountWrapper>
                      </>
                    ) : (
                      ''
                    )}
                  </Table.Cell>
                  <Table.Cell align="right">
                    <ResultButton
                      transaction={row}
                      status={status}
                      dispatch={dispatch}
                      fetchTransactions={fetchTransactions}
                      setApiResult={setApiResult}
                    />
                  </Table.Cell>
                </Table.Row>
              );
            })}
          </Table.Body>
          <Table.Footer>
            <Table.Row noBorder>
              <Table.Cell colSpan={8} align="center" styles={{ paddingTop: '16px' }}>
                <Pager pagination={transactions.pagination} onChangePage={fetchTransactions} />
              </Table.Cell>
            </Table.Row>
          </Table.Footer>
        </Table>
      )}
    </>
  );
};

const ResultButton = ({ transaction, status, dispatch, fetchTransactions, setApiResult }) => {
  const user = useAppSelector((state) => state.user);

  const [isCanceling, setIsCanceling] = useState(false);

  const fetchTransactionJob = (t) => {
    const { id } = t;
    const params = { per_page: 99999 };
    return getTransactionJob(id, toQueryParams(params));
  };

  const csvFileName = (id) => `bulk_transactions_${formatUUID(id)}.csv`;

  const renderProcessButton = () => {
    if (CANCELABLE_STATUSES.includes(status.name)) {
      const cancelFunc = () => {
        setIsCanceling(true);
        cancelBulkTransaction(transaction.id)
          .then(() => fetchTransactions())
          .catch((err) =>
            Promise.resolve(err).then((error) => {
              setApiResult([
                {
                  text: error.message,
                  isError: true,
                },
              ]);
            })
          )
          .finally(() => {
            setIsCanceling(false);
            setTimeout(() => setApiResult(undefined), 5000);
          });
      };

      return (
        <Button
          variant="muted-outline"
          disabled={isCanceling}
          onClick={() => {
            if (!CANCELATION_BEHAVIORS[status.name].skipConfirmation) {
              dispatch(
                openAlertModal({
                  body: <span>本当に削除してよろしいですか？</span>,
                  confirmText: '実行',
                  confirmColor: 'danger',
                  onConfirm: () => cancelFunc(),
                })
              );
            } else {
              // skip confirmation for other cancelable status due to high level of urgency
              cancelFunc();
            }
          }}
        >
          {CANCELATION_BEHAVIORS[status.name].label}
        </Button>
      );
    }

    if (status.name === 'transaction-canceled' && user.isServiceUser()) {
      return (
        <Button
          variant="outline"
          onClick={() =>
            dispatch(
              openAlertModal({
                body: <span>処理を再開しますか？</span>,
                confirmText: '実行',
                onConfirm: () =>
                  resumeBulkTransaction(transaction.id)
                    .then(() => fetchTransactions())
                    .catch((err) =>
                      Promise.resolve(err).then((error) => {
                        setApiResult([
                          {
                            text: error.message,
                            isError: true,
                          },
                        ]);
                      })
                    )
                    .finally(() => {
                      setTimeout(() => setApiResult(undefined), 5000);
                    }),
              })
            )
          }
        >
          <FA icon="play" /> 再開
        </Button>
      );
    }
    return null;
  };

  const renderCSVButton = () => {
    return (transaction.total_count ?? 0) > 0 ||
      (status.name === 'transaction-error' && !transaction.error) ? (
      <CSVButton
        fetchData={() =>
          fetchTransactionJob(transaction).then((result) =>
            result.rows.map(
              ({ bulk_transaction, created_at, updated_at, point_expires_at, ...rest }) => ({
                ...rest,
                created_at: formatJstFromUtc(created_at),
                updated_at: formatJstFromUtc(updated_at),
                point_expires_at: formatJstFromUtc(point_expires_at),
                bulk_transaction_id: bulk_transaction.id,
              })
            )
          )
        }
        fileName={csvFileName(transaction.id)}
      />
    ) : null;
  };

  return (
    <ActionButtonsWrapper>
      {renderProcessButton()}
      {renderCSVButton()}
    </ActionButtonsWrapper>
  );
};

const mapStateToProps = (state) => ({
  user: state.user,
});

export default connect(mapStateToProps)(DirectRewards);
